// src/hooks/useProductManager.tsx
// Reads products directly from MySQL via API — no IndexedDB.
import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { type StoreName } from '../utils/db';
import {
  exportToExcel as excelExportToExcel,
  exportToExcelAdvanced as excelExportToExcelAdvanced,
} from '../utils/excel';
import type { ExcelExportOptions } from '../utils/excel';
import { apiCreateProduct, apiUpdateProduct, apiDeleteProduct, isMysqlBacked } from '../services/productAPI';
import { SYNC_CATEGORIES } from '../services/productSync';
import { API_BASE } from '../config';
export type { ExcelColumn, ExcelExportOptions } from '../utils/excel';

export function useProductManager<T extends { id: IDBValidKey }>(storeName: StoreName) {
  const [items, setItems]               = useState<T[]>([]);
  const [loading, setLoading]           = useState(false);
  const [currentItem, setCurrentItem]   = useState<T | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const refresh = useCallback(async () => {
    const cats = SYNC_CATEGORIES.filter(c => c.storeName === storeName);
    if (cats.length === 0) { setItems([]); return; }
    setLoading(true);
    try {
      const all: T[] = [];
      for (const cat of cats) {
        const res = await fetch(`${API_BASE}/product/${cat.apiName}`);
        if (!res.ok) throw new Error(`Сервер вернул ${res.status}`);
        const data: unknown[] = await res.json();
        all.push(...data.map(row => cat.mapper(row as Record<string, unknown>) as T));
      }
      setItems(all);
    } catch (err) {
      message.error(`Ошибка загрузки товаров: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [storeName]);

  useEffect(() => { refresh(); }, [refresh]);

  const addItem = useCallback(async (item: T) => {
    try {
      if (isMysqlBacked(item as any)) {
        await apiUpdateProduct(item as any, storeName);
      } else {
        const newId = await apiCreateProduct(item as any, storeName);
        (item as any).id = newId;
      }
      await refresh();
      setCurrentItem(null);
      setIsModalVisible(false);
    } catch (err) {
      message.error(`Ошибка сохранения: ${(err as Error).message}`);
      throw err;
    }
  }, [storeName, refresh]);

  const deleteItem = useCallback(async (key: IDBValidKey) => {
    try {
      const item = items.find(i => String((i as any).id) === String(key));
      if (item) await apiDeleteProduct(item as any, storeName);
      await refresh();
    } catch (err) {
      message.error(`Ошибка удаления: ${(err as Error).message}`);
    }
  }, [storeName, refresh, items]);

  const exportToExcel = useCallback(async (filename: string) => {
    excelExportToExcel(items, filename, 'Sheet1');
  }, [items]);

  const exportToExcelAdvanced = useCallback(async (opts: ExcelExportOptions<T>) => {
    const data = opts.data ?? items;
    await excelExportToExcelAdvanced({ ...opts, data });
  }, [storeName, items]);

  return {
    items,
    loading,
    currentItem,
    setCurrentItem,
    isModalVisible,
    setIsModalVisible,
    addItem,
    deleteItem,
    exportToExcel,
    exportToExcelAdvanced,
    refresh,
  };
}
